import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { appRoles } from '../../authConfig';
import MultiSelect from '../../common/MultiSelect';
import PagedList, { IPagedListRefType } from '../../common/PagedList';
import {
  deleteRefValue,
  exportReferenceValues,
  IImportReferenceValuesResponse,
  IReferenceValue,
  importReferenceValues,
  getReferenceValues,
  getReferences,
  orderReferenceValues,
} from '../../services/referenceService';
import { useAtomValue, useSetAtom } from 'jotai';
import { profileDataAtom } from '../../atoms/authAtoms';
import { useNavigate } from 'react-router-dom';
import { getTheme, Panel, PanelType, PrimaryButton } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  Button,
  Dropdown,
  Field,
  FluentProvider,
  Option,
  Text,
  webLightTheme,
} from '@fluentui/react-components';
import { ArrowDownRegular, ArrowUpRegular } from '@fluentui/react-icons';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';

const panelTheme = getTheme();

const References = () => {
  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();
  const [selectedRefId, setSelectedRefId] = useState<string>();
  const pagedListRef = useRef<IPagedListRefType>(null);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const [references, setReferences] = useState<string[]>([]);
  const [refName, setRefName] = useState<string>();
  const [panelMode, setPanelMode] = useState<'import' | 'export'>();
  const [selectedExportRefNames, setSelectedExportRefNames] = useState<
    string[]
  >([]);
  const [importFileName, setImportFileName] = useState('');
  const [importCsvContent, setImportCsvContent] = useState('');
  const [importResponse, setImportResponse] =
    useState<IImportReferenceValuesResponse>();
  const [orderItems, setOrderItems] = useState<IReferenceValue[]>([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [isReorderOpen, { setTrue: showReorder, setFalse: hideReorder }] =
    useBoolean(false);

  const isAdmin = profileData.roles.includes(appRoles.Admin);

  useEffect(() => {
    const fetchData = async () => {
      const abortController = new AbortController();
      setIsInProgress(true);
      try {
        const references: string[] = await getReferences(abortController);
        setReferences(references);
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
  }, [setErrorMessage, setIsInProgress]);

  const hidePanel = () => {
    setPanelMode(undefined);
    setImportResponse(undefined);
  };

  const openExportPanel = () => {
    setPanelMode('export');
    setImportResponse(undefined);
    setSelectedExportRefNames(refName ? [refName] : []);
  };

  const openImportPanel = () => {
    setPanelMode('import');
    setImportResponse(undefined);
    setImportCsvContent('');
    setImportFileName('');
  };

  const handleImportFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImportFileName('');
      setImportCsvContent('');
      return;
    }

    const reader = new FileReader();
    setImportFileName(file.name);
    reader.onload = () => {
      setImportCsvContent((reader.result as string) ?? '');
    };
    reader.onerror = () => {
      setImportCsvContent('');
      setErrorMessage('Failed to read the selected CSV file.');
    };
    reader.readAsText(file);
  };

  const downloadExportCsv = (csvContent: string) => {
    const fileName = `reference-values-${new Date()
      .toISOString()
      .replace(/[:.]/g, '-')}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportReferenceValues = async () => {
    if (selectedExportRefNames.length === 0) {
      return;
    }

    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      const csvContent = await exportReferenceValues(
        abortController,
        selectedExportRefNames
      );
      downloadExportCsv(csvContent);
      setSuccessMessage('Export completed. CSV downloaded.');
      hidePanel();
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
  };

  const handleImportReferenceValues = async () => {
    if (!importCsvContent) {
      return;
    }

    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      const result = await importReferenceValues(abortController, {
        csvContent: importCsvContent,
      });
      setImportResponse(result);
      if (result.errors.length > 0) {
        setErrorMessage(result.errors.join('\n'));
      } else {
        setSuccessMessage(
          `Import completed. Created: ${result.createdCount}, Updated: ${result.updatedCount}.`
        );
        pagedListRef.current?.refresh();
      }
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
  };

  return (
    <>
      <FluentProvider theme={webLightTheme}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: 420,
            marginBottom: 8,
          }}
        >
          <Text style={{ minWidth: 96 }}>Reference</Text>
          <Dropdown
            style={{ width: 300 }}
            value={refName ?? ''}
            selectedOptions={refName ? [refName] : []}
            onOptionSelect={(_event, data) => {
              const selectedRefName = data.optionValue?.toString();
              setRefName(selectedRefName);
              setSelectedRefId(undefined);
              pagedListRef.current?.refresh();
            }}
          >
            {references.map((ref) => (
              <Option key={ref} value={ref}>
                {ref}
              </Option>
            ))}
          </Dropdown>
        </div>
      </FluentProvider>
      <PagedList
        selectedTab="References"
        ref={pagedListRef}
        searchInToolbar={true}
        searchInputWidth={220}
        columns={[
          { key: 'id', minWidth: 45, maxWidth: 50 },
          { key: 'key', minWidth: 75, maxWidth: 100 },
          { key: 'value', minWidth: 200, maxWidth: 250 },
          { key: 'parentRef', minWidth: 100, maxWidth: 150 },
          { key: 'parentKey', minWidth: 75, maxWidth: 100 },
        ]}
        getAction={(
          abortController: AbortController,
          pageSize: number,
          pageNo: number,
          orderBy?: string | undefined,
          search?: string | undefined
        ) => {
          return getReferenceValues(
            abortController,
            pageSize,
            pageNo,
            refName ?? '',
            orderBy,
            search
          );
        }}
        commandBarItems={[
          {
            key: 'newRefValue',
            text: 'New',
            iconProps: { iconName: 'Add' },
            onClick: () => {
              void navigate(`/newRefValue?refName=${refName}`);
            },
            disabled: !isAdmin,
          },
          {
            key: 'editRefValue',
            text: 'Edit',
            iconProps: { iconName: 'Edit' },
            onClick: () => {
              void navigate(`/editRefValue?id=${selectedRefId}`);
            },
            disabled: !isAdmin || !selectedRefId,
          },
          {
            key: 'deleteRefValue',
            text: 'Delete',
            iconProps: { iconName: 'Delete' },
            onClick: () => {
              if (selectedRefId) {
                const abortController = new AbortController();
                deleteRefValue(abortController, Number(selectedRefId)).then(
                  () => {
                    pagedListRef.current?.refresh();
                    setSuccessMessage(
                      `Reference Value with id: ${selectedRefId} deleted.`
                    );
                  }
                );
              }
            },
            disabled: !isAdmin || !selectedRefId,
          },
          {
            key: 'reorderRefValues',
            text: 'Reorder',
            iconProps: { iconName: 'Sort' },
            onClick: () => {
              if (!refName) return;
              const abortController = new AbortController();
              getReferenceValues(abortController, 1000, 1, refName, 'sortOrder')
                .then((page) => {
                  setOrderItems(page.items ?? []);
                  showReorder();
                })
                .catch((error: { message?: string }) => {
                  setErrorMessage(
                    error.message ?? 'Failed to load reference values.'
                  );
                });
            },
            disabled: !isAdmin || !refName,
          },
          {
            key: 'importRefValues',
            text: 'Import',
            iconProps: { iconName: 'Upload' },
            onClick: () => openImportPanel(),
            disabled: !isAdmin,
          },
          {
            key: 'exportRefValues',
            text: 'Export',
            iconProps: { iconName: 'CloudDownload' },
            onClick: () => openExportPanel(),
            disabled: !isAdmin,
          },
        ]}
        onSelectionChange={(selection: any) => {
          setSelectedRefId(selection.id);
        }}
      ></PagedList>
      <Panel
        isOpen={!!panelMode}
        onDismiss={hidePanel}
        type={PanelType.smallFixedFar}
        closeButtonAriaLabel="Close"
        isFooterAtBottom
        styles={{
          scrollableContent: { minHeight: 0 },
          footer: {
            backgroundColor: panelTheme.semanticColors.bodyBackground,
            borderTopColor: panelTheme.semanticColors.variantBorder,
            zIndex: 1,
          },
        }}
        onRenderFooterContent={() => (
          <FluentProvider theme={webLightTheme}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              {panelMode === 'export' ? (
                <Button
                  appearance="primary"
                  onClick={() => {
                    void handleExportReferenceValues();
                  }}
                  disabled={selectedExportRefNames.length === 0}
                >
                  Export
                </Button>
              ) : (
                <Button
                  appearance="primary"
                  onClick={() => {
                    void handleImportReferenceValues();
                  }}
                  disabled={!importCsvContent}
                >
                  Import
                </Button>
              )}
              <Button appearance="secondary" onClick={hidePanel}>
                Cancel
              </Button>
            </div>
          </FluentProvider>
        )}
        headerText={
          panelMode === 'export'
            ? 'Export reference values'
            : 'Import reference values'
        }
      >
        <FluentProvider theme={webLightTheme}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {panelMode === 'export' ? (
              <MultiSelect
                label="References"
                placeholder="Select references"
                options={references.map((name) => ({
                  value: name,
                  label: name || '(empty)',
                }))}
                selected={selectedExportRefNames}
                onChange={setSelectedExportRefNames}
              />
            ) : (
              <>
                <Field label="CSV file">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleImportFileChange}
                  />
                </Field>
                <Text size={200}>{importFileName || 'No file selected'}</Text>
              </>
            )}
            {importResponse ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <Text size={200}>Created: {importResponse.createdCount}</Text>
                <Text size={200}>Updated: {importResponse.updatedCount}</Text>
                {importResponse.errors.length > 0 ? (
                  <>
                    <Text size={200}>Errors:</Text>
                    {importResponse.errors.map((error, index) => (
                      <Text key={`${error}-${index}`} size={200}>
                        {error}
                      </Text>
                    ))}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </FluentProvider>
      </Panel>
      <Panel
        isOpen={isReorderOpen}
        onDismiss={hideReorder}
        type={PanelType.medium}
        headerText={`Reorder ${refName ?? ''}`}
        isFooterAtBottom
        onRenderFooterContent={() => (
          <PrimaryButton
            text="Save"
            disabled={orderSaving || orderItems.length === 0}
            onClick={() => {
              if (!refName) return;
              const abortController = new AbortController();
              setOrderSaving(true);
              orderReferenceValues(
                abortController,
                refName,
                orderItems.map((item) => item.id)
              )
                .then(() => {
                  hideReorder();
                  pagedListRef.current?.refresh();
                  setSuccessMessage('Reference value order saved.');
                })
                .catch((error: { message?: string }) => {
                  setErrorMessage(error.message ?? 'Failed to save order.');
                })
                .finally(() => setOrderSaving(false));
            }}
          />
        )}
      >
        <FluentProvider theme={webLightTheme}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginTop: 16,
            }}
          >
            {orderItems.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 12px',
                  border: '1px solid #e1dfdd',
                  borderRadius: 4,
                }}
              >
                <span>
                  {item.key} {item.value}
                </span>
                <span style={{ display: 'flex', gap: 4 }}>
                  <Button
                    appearance="subtle"
                    icon={<ArrowUpRegular />}
                    disabled={index === 0}
                    aria-label="Move up"
                    onClick={() =>
                      setOrderItems((rows) => {
                        if (index === 0) return rows;
                        const next = [...rows];
                        const [row] = next.splice(index, 1);
                        next.splice(index - 1, 0, row);
                        return next;
                      })
                    }
                  />
                  <Button
                    appearance="subtle"
                    icon={<ArrowDownRegular />}
                    disabled={index === orderItems.length - 1}
                    aria-label="Move down"
                    onClick={() =>
                      setOrderItems((rows) => {
                        if (index === rows.length - 1) return rows;
                        const next = [...rows];
                        const [row] = next.splice(index, 1);
                        next.splice(index + 1, 0, row);
                        return next;
                      })
                    }
                  />
                </span>
              </div>
            ))}
          </div>
        </FluentProvider>
      </Panel>
    </>
  );
};

export default References;
